import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { CalendarDays, Loader2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { format } from 'date-fns';

interface SimpleCalendarProps {
  customerId: number;
  customerName: string;
  currentDate?: string | Date | null;
  onDateSelected?: (date: Date) => void;
  appointmentType: 'initialContact' | 'customerMeeting';
  customerEmail?: string;
  customerPhone?: string;
  customerAddress?: string;
  notes?: string;
}

/**
 * Simple Calendar Embedding Component for Cal.com
 * This provides a simple, reliable way to embed Cal.com calendars
 */
const SimpleCalendar: React.FC<SimpleCalendarProps> = ({
  customerId,
  customerName,
  currentDate,
  onDateSelected,
  appointmentType,
  customerEmail,
  customerPhone,
  customerAddress,
  notes,
}) => {
  const { toast } = useToast();
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [theme, setTheme] = useState('light');

  // Detect dark mode and update when it changes
  useEffect(() => {
    // Initial check
    const updateTheme = () => {
      const htmlElement = document.documentElement;
      const hasDarkClass = htmlElement.classList.contains('dark');
      setTheme(hasDarkClass ? 'dark' : 'light');
    };

    // Set initial theme
    updateTheme();

    // Create a mutation observer to detect theme changes
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          updateTheme();
        }
      });
    });

    // Start observing the document for class changes
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    });

    // Clean up
    return () => observer.disconnect();
  }, []);

  // Format date for display
  const formattedDate = currentDate
    ? new Date(currentDate).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : null;

  // Handle message from Cal.com iframe
  const handleCalMessage = (e: MessageEvent) => {
    if (e.data && e.data.type === 'CAL:BOOKING_SUCCESSFUL') {
      console.log('Cal.com booking successful:', e.data);

      if (e.data.data && e.data.data.date) {
        const bookingDate = new Date(e.data.data.date);

        if (onDateSelected) {
          onDateSelected(bookingDate);
        }

        toast({
          title: 'Appointment Booked',
          description: `Successfully booked ${appointmentType === 'initialContact' ? 'initial contact' : 'customer meeting'} for ${format(bookingDate, 'PPpp')}`,
        });

        setCalendarOpen(false);
      }
    }
  };

  // Add event listener when dialog opens
  useEffect(() => {
    if (calendarOpen) {
      setIsLoading(true);

      // Refresh theme when calendar opens
      const htmlElement = document.documentElement;
      const hasDarkClass = htmlElement.classList.contains('dark');
      setTheme(hasDarkClass ? 'dark' : 'light');

      window.addEventListener('message', handleCalMessage);

      return () => {
        window.removeEventListener('message', handleCalMessage);
      };
    }
  }, [calendarOpen]);

  // Create calendar notes
  const calendarNotes = `${appointmentType === 'initialContact' ? 'Initial Contact' : 'Customer Meeting'} for ${customerName}

Customer ID: ${customerId}
${customerEmail ? `Email: ${customerEmail}` : ''}
${customerPhone ? `Phone: ${customerPhone}` : ''}
${customerAddress ? `Address: ${customerAddress}` : ''}
${notes ? `\nAdditional Notes: ${notes}` : ''}`;

  return (
    <Dialog open={calendarOpen} onOpenChange={setCalendarOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={`w-full justify-start gap-2 ${theme === 'dark' ? 'dark-mode-calendar-trigger' : 'light-mode-calendar-trigger'}`}
        >
          <CalendarDays className="h-4 w-4" />
          {formattedDate
            ? formattedDate
            : appointmentType === 'initialContact'
              ? 'Schedule Initial Contact'
              : 'Schedule Customer Meeting'}
        </Button>
      </DialogTrigger>

      {calendarOpen && (
        <DialogContent
          className={`sm:max-w-[450px] p-0 overflow-hidden max-h-[600px] ${theme === 'dark' ? 'bg-slate-900 text-white' : 'bg-white text-slate-900'}`}
        >
          <DialogHeader className="p-4 pb-2">
            <DialogTitle>
              {appointmentType === 'initialContact'
                ? 'Schedule Initial Contact'
                : 'Schedule Customer Meeting'}
            </DialogTitle>
            <DialogDescription>
              Book a 30-minute{' '}
              {appointmentType === 'initialContact' ? 'initial contact' : 'customer meeting'} with{' '}
              {customerName}
            </DialogDescription>
          </DialogHeader>

          <div className="relative h-[500px]">
            {isLoading && (
              <div className="absolute inset-0 flex items-center justify-center bg-background/50 z-10">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            )}

            <iframe
              src={`https://cal.com/mquinton34/30min?name=${encodeURIComponent(customerName)}&notes=${encodeURIComponent(calendarNotes)}&embed=true&layout=week_view&theme=${theme}&hideBranding=true&hideEventTypeDetails=false`}
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
                borderRadius: '0.5rem',
                backgroundColor: theme === 'dark' ? '#1f2937' : '#ffffff',
              }}
              className={theme === 'dark' ? 'dark-calendar' : 'light-calendar'}
              onLoad={() => setIsLoading(false)}
              frameBorder="0"
            ></iframe>
          </div>
        </DialogContent>
      )}
    </Dialog>
  );
};

export default SimpleCalendar;
